// components/Button/Button.js
import React from "react";
import PropTypes from "prop-types";
import clsx from "clsx";

const Status = ({ status, icon, color, textcolor, asDiv }) => {
  const formatString = (input) => {
    if (typeof input !== "string") {
      // throw new Error("Input must be a string");
      return "-";
    }
    return input.trim().toLowerCase().replace(/\s+/g, "-");
  };

  const hexToRgb = (hex) => {
    // Remove the '#' if present
    const sanitizedHex = hex.replace("#", "");

    // Ensure the input is valid
    if (!/^([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/.test(sanitizedHex)) {
      return "-";
      // throw new Error("Invalid hex color format");
    }

    // If shorthand format (#RGB), expand to full (#RRGGBB)
    const fullHex = sanitizedHex.length === 3
      ? sanitizedHex
          .split("")
          .map((char) => char + char)
          .join("")
      : sanitizedHex;

    // Parse the R, G, B values
    const r = parseInt(fullHex.slice(0, 2), 16);
    const g = parseInt(fullHex.slice(2, 4), 16);
    const b = parseInt(fullHex.slice(4, 6), 16);

    return `${r}, ${g}, ${b}`;
  };

  const computedColor = (colour) => {

    const rgbcolor = hexToRgb(colour);

    // const [r, g, b] = rgbcolor.match(/\d+/g).map((value) => parseInt(value, 10));

    // const componentToHex = (c) => c.toString(16).padStart(2, "0");

    // const rgbToHex = (r, g, b) => `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`;

    // const hexColor = rgbToHex(r, g, b);

    // const luminance = Math.round((r * 299 + g * 587 + b * 114) / 1000);

    // const textcolor = luminance > 180 ? "#000000" : "#ffffff";

    return { backgroundColor: 'rgba('+rgbcolor+', 0.15)', color: 'rgba('+rgbcolor+',1)' };

  }

  const statusName = formatString(status);
  // When asDiv is true, don't wrap in extra div to allow clicks to bubble properly
  if (asDiv) {
    return color ? (
      <StatusButton
        style={computedColor(color)}
        text={status}
        icon={icon}
        asDiv={asDiv}
      />
    ) : (
      <StatusList status={statusName} label={status} icon={icon} asDiv={asDiv} />
    );
  }
  
  return (
    <div>
      {color ? (
        <StatusButton
          style={computedColor(color)}
          text={status}
          icon={icon}
          asDiv={asDiv}
        />
      ) : (
        <StatusList status={statusName} label={status} icon={icon} asDiv={asDiv} />
      )}
    </div>
  );
};

Status.propTypes = {
  status: PropTypes.string.isRequired,
  icon: PropTypes.element,
  color: PropTypes.string,
  textcolor: PropTypes.string,
  asDiv: PropTypes.bool,
};

const StatusList = ({ status, label, icon, asDiv }) => {
  switch (status) {
    case "active":
      return (
        <StatusButton
          className="bg-[#009006]/10 text-[#009006]"
          text={label}
          icon={icon}
          asDiv={asDiv}
        />
      );
      case "approved":
      return (
        <StatusButton
          className="bg-[#009006]/10 text-[#009006]"
          text={label}
          icon={icon}
          asDiv={asDiv}
        />
      );
    case "inactive":
      return (
        <StatusButton
          className="bg-[#D30700]/10 text-[#D30700]"
          text={status}
          label={label}
          icon={icon}
          asDiv={asDiv}
        />
      );
    case "cancelled":
      return (
        <StatusButton
          className="bg-[#D30700]/10 text-[#D30700]"
          text={label}
          icon={icon}
          asDiv={asDiv}
        />
      );
    case "refund":
      return (
        <StatusButton
          className="bg-[#D30700]/10 text-[#D30700]"
          text={label}
          icon={icon}
          asDiv={asDiv}
        />
      );
    case "refunded":
      return (
        <StatusButton
          className="bg-[#D30700]/10 text-[#D30700]"
          text={label}
          icon={icon}
          asDiv={asDiv}
        />
      );
    case "failed":
      return (
        <StatusButton
          className="bg-[#D30700]/10 text-[#D30700]"
          text={label}
          icon={icon}
          asDiv={asDiv}
        />
      );
    case "pending":
      return (
        <StatusButton
          className="bg-[#00C2FF]/15 text-[#00C2FF]"
          text={label}
          icon={icon}
          asDiv={asDiv}
        />
      );
      case "paid":
      return (
        <StatusButton
          className="bg-[#009006]/10 text-[#009006]"
          text={label}
          icon={icon}
          asDiv={asDiv}
        />
      );
    default:
      return (
        <StatusButton
          className="bg-gray-200 text-gray-500"
          text={label}
          icon={icon}
          asDiv={asDiv}
        />
      );
  }
};
const StatusButton = ({ text, className, icon, style, onClick, asDiv }) => {
  const baseClasses = clsx(
    `inline-flex items-center px-4 py-1 text-sm font-medium rounded-full capitalize break-keep`,
    `${className}`
  );

  // If asDiv is true, render as div instead of button (for dropdown triggers)
  if (asDiv) {
    return (
      <div
        className={baseClasses}
        style={style}
        onClick={(e) => {
          // Don't handle click here - let it bubble to parent DropdownAction
          // Only call onClick if explicitly provided
          if (onClick) {
            onClick(e);
          }
          // Don't prevent default or stop propagation - allow parent to handle
        }}
      >
        {text} {icon && <span className="inline-block ml-2">{icon}</span>}
      </div>
    );
  }

  return (
    <button
      type="button"
      className={baseClasses}
      style={style}
      onClick={(e) => {
        // If onClick is provided, call it, otherwise allow event to bubble
        if (onClick) {
          onClick(e);
        }
        // Don't prevent default or stop propagation to allow parent handlers to work
      }}
    >
      {text} {icon && <span className="inline-block ml-2">{icon}</span>}
    </button>
  );
};

StatusButton.propTypes = {
  text: PropTypes.string.isRequired,
  className: PropTypes.string,
  icon: PropTypes.element,
  color: PropTypes.string,
  style: PropTypes.object,
  onClick: PropTypes.func,
  asDiv: PropTypes.bool,
};
StatusList.propTypes = {
  status: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  icon: PropTypes.element,
};

export default Status;
